import React, { useEffect, useMemo, useState } from 'react';
import TrendCard from '../TrendCard';
import Chart from './chart';
import { canRequest, channelColorMap } from '../utils';
import { getStudioAccounts } from '@op/services';
import './index.scss';
import { getPercentageInfo, handleMentionData } from '@op/utils';
import { reviewsFormat } from '@op/utils/value';
import { useIntl } from 'umi';
import { chartAllColors } from '@op/constants';
import { dealwithGrowthChartRatio } from '@op/pages/accounts/utils';
import { formatTimeToShow, getNeedTime } from '@op/utils/time';
import { Bubble } from 'tea-component';
import { getOpinionFilterCond } from '@op/services/filter';

import { channelInfo } from '@op/components/ChannelLabel';
import { studioAccountsPageOfficialAccounts } from '@op/components/FilterCommon/utils/monitorUtil';

export default function OfficialAccounts({ filter }: { filter: any; }) {
  const { formatMessage } = useIntl();
  const sortChartData = (newArr: any[]) => {
    newArr.sort((a, b) => a.name.toLocaleLowerCase() === 'others'
      ? 1
      : b.name.toLocaleLowerCase() === 'others'
        ? -1
        : 0);
  };
  const [xAxisData, setXAxisData] = useState<any>([]);
  const [seriesDataArray, setSeriesDataArray] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [monitory, setMonitory] = useState(null);
  // 处理增长量数据方法
  const dealwithGrowth = (
    element: Array<any>,
    timeline_of_start_time: string,
    timeline_of_end_time: string,
  ) => {
    const dateType = filter.date_type;
    const needTime = getNeedTime(dateType) as any;
    // 处理增长量数据
    const { table } = dealwithGrowthChartRatio(
      timeline_of_start_time,
      timeline_of_end_time,
      (element || []).map((item) => ({ ...item, name: item.key })),
      dateType,
    );

    // eslint-disable-next-line prefer-const
    let { newArr, timeArr } = handleMentionData([...(table || [])], needTime);
    newArr = newArr.map((item: { name: string | number; }, index: number) => ({
      ...item,
      legendName: item.name,
      color: channelColorMap[item.name as any] || chartAllColors[index],
    }));
    // 将other排序到最后
    sortChartData(newArr);
    setXAxisData(timeArr);
    setSeriesDataArray(newArr);
  };

  const [accountFollowers, setAccountFollowers] = useState<
  Array<{ name: string; value: number; color: string; percent: string; }>
  >([]);
  const [accountTotal, setAccountTotal] = useState<number>(0);

  function setAccountFollowersInfo(list: any = {}) {
    let channelList = [];
    let total = 0;
    for (const key in list) {
      const element = list[key] || 0;

      total += element;
      channelList.push({ name: key, value: element });
    }
    channelList.sort((a, b) => b.value - a.value);
    channelList = channelList.map((item, index) => ({
      ...item,
      percent: getPercentageInfo(item.value, total, 2),
      color: channelColorMap[item.name as any] || chartAllColors[index],
    }));
    setAccountTotal(total);
    setAccountFollowers(channelList);
  }

  // 请求monitory数据，用于官方账号参数同舆情一致
  useEffect(() => {
    const { edition_unified_id, entity_type, id_type } = filter || {};
    getOpinionFilterCond({
      edition_unified_id,
      entity_type,
      id_type,
      request_type: ["monitor_by_account_category"],
      un_request_type: ["topic_trans"],
    }).then((res: any) => {
      const { monitor_by_account_category } = res || {};
      const { monitory } = monitor_by_account_category || {};
      setMonitory(monitory || {});
    }).catch((err: any) => {
      console.log(err);
      setMonitory(null);
    });
  }, []);

  function requestServerData() {
    setLoading(true);
    const accounts = studioAccountsPageOfficialAccounts(monitory);
    const accountList: string[] = [];
    Object.keys(accounts)?.forEach(key => {
      accounts[key]?.forEach((acc: any) => {
        accountList.push(acc.account);
      });
    });
    const params = {
      ...filter,
      accounts: accountList
    };
    getStudioAccounts(params)
      .then((data: any) => {
        const {
          list,
          timeline,
          timeline_of_start_time = '2024-08-01 00:00:00',
          timeline_of_end_time = '2024-08-08 00:00:00',
        } = data || {};
        // list;
        setAccountFollowersInfo(list);
        dealwithGrowth(timeline, timeline_of_start_time, timeline_of_end_time);
      }).catch(() => {}).finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    if (canRequest(filter) && monitory) {
      requestServerData();
    }
  }, [filter, monitory]);

  function getBubbleContentText() {
    if (accountFollowers.every((item) => item.value === 0)) {
      return null;
    }
    return (
      <div className="chart-common-formatter">
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>{formatTimeToShow(filter)}</th>
              <th style={{ textAlign: 'right' }}>
                {formatMessage({ id: 'Followers' })}
              </th>
              <th style={{ textAlign: 'right' }}>
                {formatMessage({ id: 'Share-1' })}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td style={{ textAlign: 'left' }}>
                {formatMessage({ id: 'Sum' })}
              </td>
              <td style={{ fontWeight: 'bold', textAlign: 'right' }}>
                {reviewsFormat(accountTotal)}
              </td>
              <td style={{ fontWeight: 'bold', textAlign: 'right' }}>100%</td>
            </tr>
            {accountFollowers.map((item) => {
              const { name } = channelInfo(item.name as any);
              return (
                <tr key={item.name}>
                  <td style={{ textAlign: 'left' }}>
                    <span
                      style={{ backgroundColor: item.color }}
                      className="matter-dotter"
                    ></span>
                    {name}
                  </td>
                  <td style={{ fontWeight: 'bold', textAlign: 'right' }}>
                    {reviewsFormat(item.value)}
                  </td>
                  <td style={{ fontWeight: 'bold', textAlign: 'right' }}>
                    {item.percent}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  const empty = useMemo(() => xAxisData.length === 0 || accountTotal === 0, [accountTotal, xAxisData]);

  return (
    <TrendCard
      moduleType="accounts"
      filter={filter}
      loading={loading}
      empty={empty}
      loadingHeight={147}
      title="Official Accounts"
      className="dbh-trend-card--official"
    >
      <div className="single-line-box">
        <div>
          {formatMessage({ id: 'Official Account Followers' })}:{' '}
          {reviewsFormat(accountTotal)}
        </div>
        <Bubble
          content={getBubbleContentText()}
          className="only-line-bar-box-bubble"
        >
          <div className="only-line-bar-box">
            {accountFollowers.map((item, index) => {
              const isBegin = index === 0;
              const isEnd = index + 1 === accountFollowers.length;
              let className = '';
              if (isBegin) className += ' first';
              if (isEnd) className += ' end';
              return (
                <span
                  className={`only-line-bar-item ${className}`}
                  key={item.name}
                  style={{
                    backgroundColor: item.color,
                    width: item.percent,
                  }}
                ></span>
              );
            })}
          </div>
        </Bubble>
      </div>
      <div className="chart-box">
        <Chart
          xAxisData={xAxisData}
          seriesDataArray={seriesDataArray}
          height={120}
        ></Chart>
      </div>
    </TrendCard>
  );
}
